
import React from 'react';
import { LineChart, Bot, Users, Wallet } from 'lucide-react';
import { EnhancedStatsCard } from '@/components/dashboard/EnhancedStatsCard';
import { MobileStatsCard } from '@/components/dashboard/MobileStatsCard';
import { EnhancedDashboardHeader } from '@/components/dashboard/EnhancedDashboardHeader';
import { useAuth } from '@/contexts/AuthContext';
import { useResourceUsage } from '@/hooks/use-resource-usage';
import { Skeleton } from '@/components/ui/skeleton';
import { TopPerformersCard } from '@/components/dashboard/TopPerformersCard';
import { AIPropPromoCard } from '@/components/dashboard/AIPropPromoCard';
import { BetaBanner } from '@/components/common/BetaBanner';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

const Dashboard = () => {
  const { user } = useAuth();
  const { data: userStats, isLoading: isStatsLoading } = useResourceUsage(user?.id);
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const renderSkeletonStats = () => (
    <div className={`grid gap-4 ${isMobile ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 md:gap-6'}`}>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className={`p-4 md:p-6 rounded-lg bg-card animate-pulse ${isMobile ? 'aspect-square' : ''}`}>
          <div className="flex items-start justify-between">
            <div className="space-y-2 w-full">
              <Skeleton className="h-3 md:h-4 w-1/3" />
              <Skeleton className="h-5 md:h-7 w-1/2" />
              {!isMobile && <Skeleton className="h-2 md:h-3 w-1/4" />}
            </div>
            <Skeleton className="h-8 w-8 md:h-10 md:w-10 rounded-md" />
          </div>
        </div>
      ))}
    </div>
  );

  const statsCards = [
    {
      title: t('connected_accounts'),
      value: userStats?.tradingAccountsCount?.toString() || '0',
      change: t('accounts_across_all_bots'),
      positive: true,
      icon: <Users size={isMobile ? 16 : 20} />,
      colorScheme: 'primary' as const,
      progress: Math.min((userStats?.tradingAccountsCount || 0) * 10, 100)
    },
    {
      title: t('my_bots_count'),
      value: userStats?.userBotsCount?.toString() || '0',
      change: t('your_created_bots'),
      positive: true,
      icon: <Bot size={isMobile ? 16 : 20} />,
      colorScheme: 'blue' as const,
      progress: Math.min((userStats?.userBotsCount || 0) * 15, 100)
    },
    {
      title: t('prop_bots'),
      value: userStats?.propBotAccountsCount?.toString() || '0',
      change: t('connected_prop_bots'),
      positive: true,
      icon: <LineChart size={isMobile ? 16 : 20} />,
      colorScheme: 'green' as const,
      progress: Math.min((userStats?.propBotAccountsCount || 0) * 20, 100)
    },
    {
      title: t('premium_bots_count'),
      value: userStats?.premiumBotAccountsCount?.toString() || '0',
      change: t('connected_premium_bots'),
      positive: true,
      icon: <Wallet size={isMobile ? 16 : 20} />,
      colorScheme: 'purple' as const,
      progress: Math.min((userStats?.premiumBotAccountsCount || 0) * 25, 100)
    }
  ];
  
  return (
    <div className={`space-y-6 md:space-y-8 ${isMobile ? 'pb-36' : 'pb-10'} animate-fade-in`}>
      <BetaBanner />
      
      <EnhancedDashboardHeader />
      
      <div data-tour="stats-cards">
        {isStatsLoading ? renderSkeletonStats() : (
          <div className={`grid gap-4 ${isMobile ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 md:gap-6'}`}>
            {statsCards.map((card, index) => (
              <div
                key={card.title}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {isMobile ? (
                  <MobileStatsCard 
                    title={card.title}
                    value={card.value}
                    icon={card.icon}
                    colorScheme={card.colorScheme}
                  />
                ) : (
                  <EnhancedStatsCard {...card} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <div className="lg:col-span-2 animate-fade-in" style={{ animationDelay: '400ms' }}>
          <AIPropPromoCard />
        </div>
        <div className="space-y-3 md:space-y-4 animate-fade-in" style={{ animationDelay: '500ms' }}>
          <h2 className={`${isMobile ? "text-base" : "text-lg"} font-medium`}>{t('bot_performance')}</h2>
          <TopPerformersCard />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
